import { Interval } from '@ui/constants/poll';
import { queryClient } from '@ui/lib/reactQuery';
import { GET_POSITIONS_V3_BATCH } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV3Batch/query';
import { create } from 'zustand';

const FASTEST_POSITION_REFRESH_DURATION = 3_000;

let fastestRefreshTimeout: ReturnType<typeof setTimeout> | undefined;
let fastestRefreshInterval: ReturnType<typeof setInterval> | undefined;

type PositionRefreshAccelerationState = {
  fastestRefreshUntil: number;
  triggerFastestRefresh: (durationMs?: number) => void;
  clearFastestRefresh: () => void;
};

export const usePositionRefreshAccelerationStore =
  create<PositionRefreshAccelerationState>((set, get) => ({
    fastestRefreshUntil: 0,
    triggerFastestRefresh: (
      durationMs = FASTEST_POSITION_REFRESH_DURATION,
    ) => {
      const fastestRefreshUntil = Math.max(
        get().fastestRefreshUntil,
        Date.now() + durationMs,
      );

      if (fastestRefreshTimeout) {
        clearTimeout(fastestRefreshTimeout);
      }
      if (fastestRefreshInterval) {
        clearInterval(fastestRefreshInterval);
      }

      set({ fastestRefreshUntil });
      refetchPositionQueries();

      fastestRefreshInterval = setInterval(() => {
        if (get().fastestRefreshUntil <= Date.now()) return;

        refetchPositionQueries();
      }, Interval.FASTEST);

      fastestRefreshTimeout = setTimeout(() => {
        if (get().fastestRefreshUntil !== fastestRefreshUntil) return;

        set({ fastestRefreshUntil: 0 });
        fastestRefreshTimeout = undefined;
        if (fastestRefreshInterval) {
          clearInterval(fastestRefreshInterval);
          fastestRefreshInterval = undefined;
        }
      }, Math.max(fastestRefreshUntil - Date.now(), 0));
    },
    clearFastestRefresh: () => {
      if (fastestRefreshTimeout) {
        clearTimeout(fastestRefreshTimeout);
        fastestRefreshTimeout = undefined;
      }
      if (fastestRefreshInterval) {
        clearInterval(fastestRefreshInterval);
        fastestRefreshInterval = undefined;
      }

      set({ fastestRefreshUntil: 0 });
    },
  }));

export const triggerFastestPositionRefresh = () => {
  usePositionRefreshAccelerationStore.getState().triggerFastestRefresh();
};

export const getPositionRefreshInterval = ({
  isFastestRefreshActive,
  hasPendingPositionState,
}: {
  isFastestRefreshActive: boolean;
  hasPendingPositionState: boolean;
}) => {
  if (isFastestRefreshActive) return Interval.FASTEST;
  if (hasPendingPositionState) return Interval.VERYFAST;

  return Interval.NORMAL;
};

const refetchPositionQueries = () => {
  void queryClient.refetchQueries({
    queryKey: [GET_POSITIONS_V3_BATCH],
    type: 'active',
  });
};
